import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Zap, Plus, MoreHorizontal, Eye, MousePointerClick, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const emptyForm = { name: '', trigger_products: [], trigger_categories: [], offer_product_handle: '', discount_type: 'percentage', discount_value: 0, timer_minutes: 15, position: 1, is_active: true };

export default function UpsellListPage() {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchOffers = async () => {
    setLoading(true);
    try { const { data } = await api.get('/upsell-offers'); setOffers(data); } catch {}
    setLoading(false);
  };
  useEffect(() => {
    fetchOffers();
    api.get('/products?limit=100&admin=true').then(r => setProducts(r.data.products || [])).catch(() => {});
    api.get('/products/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => { setForm({ ...emptyForm }); setEditId(null); setDialogOpen(true); };
  const openEdit = (o) => { setForm({ ...o }); setEditId(o.id); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.offer_product_handle) { toast.error('Offer product is required'); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/upsell-offers/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/upsell-offers', form); toast.success('Created'); }
      setDialogOpen(false);
      fetchOffers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try { await api.delete(`/upsell-offers/${id}`); toast.success('Deleted'); fetchOffers(); }
    catch { toast.error('Delete failed'); }
  };

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };
  const labelStyle = { color: 'rgba(26,26,26,0.6)' };

  return (
    <motion.div data-testid="upsell-list-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Post-Purchase Upsells</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{offers.length} offers</p>
        </div>
        <Button data-testid="add-upsell-btn" onClick={openCreate} className="font-sans text-sm gap-2 rounded-none" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          <Plus size={15} /> New Offer
        </Button>
      </div>

      {/* Stats */}
      {offers.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Views', value: offers.reduce((s, o) => s + (o.views || 0), 0), icon: Eye },
            { label: 'Acceptances', value: offers.reduce((s, o) => s + (o.acceptances || 0), 0), icon: MousePointerClick },
            { label: 'Revenue', value: `$${offers.reduce((s, o) => s + (o.revenue || 0), 0).toFixed(2)}`, icon: DollarSign },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="p-4 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} strokeWidth={1.5} style={{ color: '#C4956A' }} />
                <span className="font-sans text-xs uppercase tracking-wider" style={{ color: 'rgba(26,26,26,0.4)' }}>{label}</span>
              </div>
              <p className="font-sans text-xl font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Offer', 'Product', 'Discount', 'Timer', 'Views', 'Accepts', 'Active', ''].map(h => (
                <th key={h} className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</td></tr>
            ) : offers.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center">
                <Zap size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.2)' }} />
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No upsell offers yet</p>
              </td></tr>
            ) : offers.map(o => (
              <tr key={o.id} className="border-t hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                <td className="p-3"><span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{o.name}</span></td>
                <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{o.offer_product_handle}</span></td>
                <td className="p-3"><span className="font-sans text-sm">{o.discount_type === 'percentage' ? `${o.discount_value}%` : `$${o.discount_value}`}</span></td>
                <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{o.timer_minutes}m</span></td>
                <td className="p-3"><span className="font-sans text-sm">{o.views || 0}</span></td>
                <td className="p-3"><span className="font-sans text-sm">{o.acceptances || 0}</span></td>
                <td className="p-3"><Switch checked={o.is_active} onCheckedChange={async () => { await api.put(`/upsell-offers/${o.id}`, { is_active: !o.is_active }); fetchOffers(); }} /></td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="p-1 rounded hover:bg-[rgba(26,26,26,0.05)]"><MoreHorizontal size={16} style={{ color: 'rgba(26,26,26,0.4)' }} /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(o)} className="font-sans text-sm">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(o.id)} className="font-sans text-sm text-red-600">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">{editId ? 'Edit Upsell Offer' : 'New Upsell Offer'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Name</Label>
              <Input data-testid="upsell-name-input" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. Add the Hand Cream" className="font-sans text-sm rounded-none" style={inputStyle} />
            </div>
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Trigger Categories</Label>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(c => (
                  <button key={c} onClick={() => {
                    const cats = form.trigger_categories || [];
                    set('trigger_categories', cats.includes(c) ? cats.filter(x => x !== c) : [...cats, c]);
                  }}
                    className="px-2.5 py-1 font-sans text-xs border transition-colors"
                    style={{
                      borderColor: (form.trigger_categories || []).includes(c) ? '#C4956A' : 'rgba(26,26,26,0.12)',
                      backgroundColor: (form.trigger_categories || []).includes(c) ? 'rgba(196,149,106,0.1)' : 'transparent',
                      color: (form.trigger_categories || []).includes(c) ? '#C4956A' : 'rgba(26,26,26,0.6)',
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Offer Product</Label>
              <Select value={form.offer_product_handle} onValueChange={v => set('offer_product_handle', v)}>
                <SelectTrigger data-testid="upsell-product-select" className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>
                  {products.map(p => <SelectItem key={p.handle} value={p.handle}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={v => set('discount_type', v)}>
                  <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Off</SelectItem>
                    <SelectItem value="specific_price">Set Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Value</Label>
                <Input type="number" step="0.01" value={form.discount_value} onChange={e => set('discount_value', parseFloat(e.target.value) || 0)}
                  className="font-sans text-sm rounded-none" style={inputStyle} />
              </div>
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Timer (min)</Label>
                <Input type="number" value={form.timer_minutes} onChange={e => set('timer_minutes', parseInt(e.target.value) || 15)}
                  className="font-sans text-sm rounded-none" style={inputStyle} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none font-sans text-sm">Cancel</Button>
            <Button data-testid="save-upsell-btn" onClick={handleSave} disabled={saving}
              className="rounded-none font-sans text-sm" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
